import _ from "lodash";
import { useMemo } from "react";
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault
} from "use-query-params";

import { ExitToApp } from "@material-ui/icons";

import Table, { TableControls } from "../Table";

type TableRow = {
  id: string;
  name: string;
  calls: number;
  voicemails: number;
};

export const USAGE_SORTS = [
  {
    key: "name_asc",
    label: "Name, A-Z"
  },
  {
    key: "name_desc",
    label: "Name, Z-A"
  },
  {
    key: "calls_desc",
    label: "Calls, Most to Least"
  },
  {
    key: "calls_asc",
    label: "Calls, Least to Most"
  },
  {
    key: "voicemails_desc",
    label: "Voicemails, Most to Least"
  },
  {
    key: "voicemails_asc",
    label: "Voicemails, Least to Most"
  }
] as const;

export type UsageSort = (typeof USAGE_SORTS)[number]["key"];

function validateSort(sort: string): UsageSort {
  return USAGE_SORTS.find(s => s.key === sort)?.key ?? USAGE_SORTS[0].key;
}

export default function UsageBreakdownTable({
  linkToRow,
  rows,
  nameTitle,
  searchTitle,
  csvExportFileName
}: {
  linkToRow: (row: TableRow) => string;
  rows: TableRow[];
  nameTitle: string;
  searchTitle: string;
  csvExportFileName: string;
}): JSX.Element {
  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0));
  const [pageSize, setPageSize] = useQueryParam(
    "page_size",
    withDefault(NumberParam, 10)
  );
  const [sort, setSort] = useQueryParam(
    "sort",
    withDefault(StringParam, USAGE_SORTS[0].key)
  );

  const [search, setSearch] = useQueryParam(
    "search",
    withDefault(StringParam, "")
  );

  const validatedSort = validateSort(sort);

  const filteredRows: TableRow[] = useMemo(() => {
    // filter
    const searchedRows = search
      ? rows.filter(({ name, calls, voicemails }) => {
          const normalizedFilter = search.trim().toLocaleLowerCase();

          if (String(calls) === normalizedFilter) {
            return true;
          }

          if (String(voicemails) === normalizedFilter) {
            return true;
          }

          if (name.toLocaleLowerCase().includes(normalizedFilter)) {
            return true;
          }

          return false;
        })
      : rows.slice(0);

    // sort
    return searchedRows.sort((a, b) => {
      switch (validatedSort) {
        case "calls_asc":
          return a.calls - b.calls;
        case "calls_desc":
          return b.calls - a.calls;
        case "voicemails_asc":
          return a.voicemails - b.voicemails;
        case "voicemails_desc":
          return b.voicemails - a.voicemails;
        case "name_asc":
          return (a.name || "").localeCompare(b.name || "");
        case "name_desc":
          return (b.name || "").localeCompare(a.name || "");
      }
    });
  }, [rows, validatedSort, search]);

  const pageRows = filteredRows.slice(page * pageSize, (page + 1) * pageSize);

  return (
    <>
      <TableControls
        sort={{
          value: sort,
          onChange: value => {
            setSort(value as UsageSort);
            setPage(0);
          },
          options: USAGE_SORTS.map(sort => ({
            value: sort.key,
            label: sort.label
          }))
        }}
        search={{
          value: search,
          onChange: value => {
            setSearch(value as string);
            setPage(0);
          },
          placeholder: searchTitle
        }}
        exportButtons={[
          {
            label: "Download CSV",
            getData: () =>
              Promise.resolve({
                fileName: csvExportFileName,
                options: {
                  columns: [nameTitle, "Calls", "Voicemails", "URL"]
                },
                data: filteredRows.map(row => ({
                  [nameTitle]: row.name,
                  Calls: row.calls,
                  Voicemails: row.voicemails,
                  URL: `${document.location.protocol}//${
                    document.location.host
                  }${linkToRow(row)}`
                }))
              })
          }
        ]}
      />
      <Table
        columns={[
          {
            key: "name",
            title: nameTitle,
            renderValue: row => row.name
          },
          {
            key: "calls",
            title: "Calls",
            renderValue: row => row.calls.toLocaleString()
          },
          {
            key: "voicemails",
            title: "Voicemails",
            renderValue: row => row.voicemails.toLocaleString()
          }
        ]}
        rows={pageRows}
        rowActions={[
          {
            type: "link",
            icon: <ExitToApp />,
            tooltipLabel: `Go To ${nameTitle}`,
            href: linkToRow
          }
        ]}
        emptyState="No results"
        totalCount={rows.length}
        rowsPerPage={pageSize}
        onChangeRowsPerPage={newRowsPerPage => {
          setPageSize(newRowsPerPage);
          setPage(0);
        }}
        page={page}
        onChangePage={setPage}
      />
    </>
  );
}
